// CustomerSearchSelect — single-select searchable combobox for picking a
// customer when creating a job. Replaces a native <select> that listed every
// customer flat. Mirrors SkuSearchSelect (controlled; parent owns the list +
// selected value; flip-up when tight; keeps an unrenderable/pre-set value).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Customer } from '@gutterinv/shared';
import { cn } from '../lib/utils.js';
import {
  filterCustomers,
  formatCustomerLabel,
  resolveSelectedCustomerLabel,
} from '../lib/customer-search.js';
import { moveHighlight } from '../lib/combobox-nav.js';

interface CustomerSearchSelectProps {
  /** Selected customer id, '' when none. */
  value: string;
  /** Customers to choose from (parent-owned query). */
  customers: Customer[];
  onChange: (customerId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}

let comboSeq = 0;

export function CustomerSearchSelect({
  value,
  customers,
  onChange,
  disabled = false,
  placeholder = 'Select customer…',
  id,
}: CustomerSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [flipUp, setFlipUp] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useMemo(() => `cust-combo-${(comboSeq += 1)}`, []);

  const selectedLabel = resolveSelectedCustomerLabel(customers, value);
  const filtered = useMemo(
    () => (open ? filterCustomers(customers, search) : []),
    [open, customers, search],
  );

  function openList() {
    if (disabled || open) return;
    setSearch('');
    const idx = value ? customers.findIndex((c) => c.id === value) : -1;
    setHighlight(idx >= 0 ? idx : 0);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setSearch('');
  }

  function pick(c: Customer) {
    onChange(c.id);
    close();
    inputRef.current?.blur();
  }

  useLayoutEffect(() => {
    if (!open) return;
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const DROPDOWN_MAX = 256; // matches max-h-64
    setFlipUp(below < Math.min(DROPDOWN_MAX, 200) && above > below);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        e.preventDefault();
        close();
        inputRef.current?.blur();
      }
      return;
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => moveHighlight(h, 1, filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => moveHighlight(h, -1, filtered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = filtered[highlight];
      if (c) pick(c);
    } else if (e.key === 'Tab') {
      close();
    }
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[highlight] ? `${listboxId}-opt-${highlight}` : undefined
        }
        readOnly={!open}
        disabled={disabled}
        value={open ? search : selectedLabel}
        placeholder={placeholder}
        onMouseDown={openList}
        onFocus={openList}
        onChange={(e) => {
          setSearch(e.target.value);
          setHighlight(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 120);
        }}
        className="flex h-9 w-full appearance-none truncate rounded-md border border-input bg-background px-3 pr-9 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          onMouseDown={(e) => {
            // Keep the input focused when grabbing the scrollbar / list chrome, so the
            // blur-timeout doesn't close the dropdown mid-drag. Option buttons handle
            // their own mousedown; act only when the target IS the list.
            if (e.target === e.currentTarget) e.preventDefault();
          }}
          className={cn(
            'absolute z-20 max-h-64 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-md',
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {filtered.map((c, idx) => (
            <li key={c.id} role="option" aria-selected={c.id === value}>
              <button
                ref={(el) => {
                  optionRefs.current[idx] = el;
                }}
                id={`${listboxId}-opt-${idx}`}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(c);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={cn(
                  'flex w-full items-center px-2 py-1.5 text-left text-sm hover:bg-accent/40',
                  idx === highlight && 'bg-accent/40',
                  c.id === value && 'font-medium',
                )}
              >
                <span className="truncate">{formatCustomerLabel(c)}</span>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-2 py-2 text-sm text-muted-foreground">
              No customers match “{search}”
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
